import React, { useEffect } from 'react';
import {
  MessageCircle, TrendingUp, BookOpen, ChevronRight,
  ExternalLink, Radar, FileText, Send, Key, Shield, Zap,
  Brain, LineChart, Users, Sparkles
} from 'lucide-react';

// WhatsApp bot number
const WHATSAPP_BOT_NUMBER = '14155238886';

const QuantClawLanding: React.FC = () => {
  const initialMessage = `Hi QuantClaw! 🦞

Quick commands:
• AAPL - Stock quote
• stats NVDA - Deep analysis
• debate [topic] - Start Think Tank
• ask Graham - Summon persona
• claw [url] - Summarize webpage
• news AAPL - Latest news
• help - All commands`;

  const magicLink = `https://wa.me/${WHATSAPP_BOT_NUMBER}?text=${encodeURIComponent(initialMessage)}`;

  // Fix body overflow for this landing page (body has overflow-hidden by default)
  useEffect(() => {
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, []);

  // Demo conversation showing unified capabilities
  const demoConversation = [
    { sender: 'claw', text: '🚀 Alert: NVDA 8-K Filing detected. Revenue beat expectations by 12%.' },
    { sender: 'user', text: 'What would Benjamin Graham think of this valuation?' },
    { sender: 'claw', text: '📚 Starting a Think Tank in Talkoru. Graham Persona analyzing intrinsic value vs. current P/E of 65...' },
    { sender: 'user', text: 'Also, any new research on intermittent fasting?' },
    { sender: 'claw', text: '🔬 Found 3 new studies from PubMed. Summarized in your Health portal. Key finding: 16:8 protocol shows improved insulin sensitivity.' },
  ];

  // Agent capabilities
  const capabilities = [
    {
      icon: <Radar className="w-6 h-6" />,
      title: 'The Scout',
      subtitle: 'Proactive Intelligence',
      desc: 'Monitors SEC filings, PubMed research, and news 24/7. Alerts you before you even ask.',
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'The Scribe',
      subtitle: 'Organized Knowledge',
      desc: 'Automatically summarizes research papers, files documents, and builds your personal knowledge base.',
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
    },
    {
      icon: <Send className="w-6 h-6" />,
      title: 'The Dispatcher',
      subtitle: 'Action on Demand',
      desc: 'Sets up Think Tanks for deep debates, executes trades, and connects your digital life.',
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white overflow-x-hidden overflow-y-auto">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="text-3xl" style={{ animation: 'pulse 2s ease-in-out infinite' }}>🦞</span>
            <span className="text-xl font-bold tracking-tight">QuantClaw</span>
            <span className="text-xs text-slate-500 hidden sm:inline">.ai</span>
          </div>
          <div className="flex items-center gap-6">
            <a
              href="https://ailabquant.com"
              className="text-sm text-slate-400 hover:text-white transition-colors hidden sm:flex items-center gap-1"
            >
              Lab <ExternalLink size={12} />
            </a>
            <a
              href="https://talkoru.com"
              className="text-sm text-slate-400 hover:text-white transition-colors hidden sm:flex items-center gap-1"
            >
              Think Tank <ExternalLink size={12} />
            </a>
            <a
              href={magicLink}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-400 text-black font-semibold px-4 py-2 rounded-lg text-sm transition-all flex items-center gap-2"
            >
              <MessageCircle size={16} />
              <span className="hidden sm:inline">Open WhatsApp</span>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-300 text-xs sm:text-sm tracking-widest uppercase mb-4 sm:mb-6">
            Your Personal Intelligence Agent
          </p>
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-extrabold mb-4 sm:mb-6 leading-tight">
            <span className="text-white">One Agent.</span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400"> Two Worlds.</span>
            <br />
            <span className="text-slate-300">Total Intelligence.</span>
          </h1>
          <p className="text-slate-300 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-8 sm:mb-12 px-2">
            QuantClaw is your proactive digital scout. It monitors the markets for wealth
            and mines the world's wisdom for life.
          </p>

          {/* Two Portals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
            {/* AILabQuant Portal */}
            <a
              href="https://ailabquant.com"
              className="group relative bg-gradient-to-br from-blue-950/50 to-blue-900/30 border border-blue-500/20 rounded-2xl p-5 sm:p-8 hover:border-blue-400/40 transition-all active:scale-[0.98] md:hover:scale-[1.02]"
            >
              <div className="absolute top-3 right-3 sm:top-4 sm:right-4 text-blue-400/50 group-hover:text-blue-400 transition-colors">
                <ExternalLink size={16} />
              </div>
              <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <LineChart className="w-6 h-6 sm:w-7 sm:h-7 text-blue-400" />
                </div>
                <div className="text-left">
                  <div className="text-xs text-blue-400 font-medium tracking-wider uppercase">Enter</div>
                  <div className="text-lg sm:text-xl font-bold">The Wealth Lab</div>
                </div>
              </div>
              <p className="text-slate-300 text-sm text-left">
                Market intelligence, technical analysis, portfolio tracking, and automated stock scouting.
              </p>
              <div className="mt-3 sm:mt-4 flex flex-wrap gap-2">
                <span className="text-xs bg-blue-500/10 text-blue-300 px-2 py-1 rounded">SEC Filings</span>
                <span className="text-xs bg-blue-500/10 text-blue-300 px-2 py-1 rounded">Deep Stats</span>
                <span className="text-xs bg-blue-500/10 text-blue-300 px-2 py-1 rounded">Alerts</span>
              </div>
            </a>

            {/* Talkoru Portal */}
            <a
              href="https://talkoru.com"
              className="group relative bg-gradient-to-br from-purple-950/50 to-purple-900/30 border border-purple-500/20 rounded-2xl p-5 sm:p-8 hover:border-purple-400/40 transition-all active:scale-[0.98] md:hover:scale-[1.02]"
            >
              <div className="absolute top-3 right-3 sm:top-4 sm:right-4 text-purple-400/50 group-hover:text-purple-400 transition-colors">
                <ExternalLink size={16} />
              </div>
              <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-purple-500/20 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 sm:w-7 sm:h-7 text-purple-400" />
                </div>
                <div className="text-left">
                  <div className="text-xs text-purple-400 font-medium tracking-wider uppercase">Enter</div>
                  <div className="text-lg sm:text-xl font-bold">The Think Tank</div>
                </div>
              </div>
              <p className="text-slate-300 text-sm text-left">
                Persona-driven debates, book-based wisdom, research synthesis, and knowledge exploration.
              </p>
              <div className="mt-3 sm:mt-4 flex flex-wrap gap-2">
                <span className="text-xs bg-purple-500/10 text-purple-300 px-2 py-1 rounded">AI Personas</span>
                <span className="text-xs bg-purple-500/10 text-purple-300 px-2 py-1 rounded">Books</span>
                <span className="text-xs bg-purple-500/10 text-purple-300 px-2 py-1 rounded">Debates</span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-slate-900/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4 text-white">Your Digital Butler</h2>
            <p className="text-slate-300 max-w-xl mx-auto text-sm sm:text-base px-2">
              Three core functions that work across your entire digital life.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {capabilities.map((cap, i) => (
              <div
                key={i}
                className="bg-slate-800/50 border border-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:border-white/10 transition-all"
              >
                <div className={`w-10 h-10 sm:w-12 sm:h-12 ${cap.bgColor} rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4`}>
                  <span className={cap.color}>{cap.icon}</span>
                </div>
                <h3 className="text-base sm:text-lg font-semibold mb-1 text-white">{cap.title}</h3>
                <p className="text-[10px] sm:text-xs text-slate-400 uppercase tracking-wider mb-2 sm:mb-3">{cap.subtitle}</p>
                <p className="text-slate-300 text-xs sm:text-sm">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WhatsApp Demo Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center">
            {/* Phone Mockup */}
            <div className="relative mx-auto w-full max-w-[280px] sm:max-w-xs order-2 md:order-1">
              <div className="bg-slate-800 rounded-[2rem] sm:rounded-[2.5rem] p-2 sm:p-3 shadow-2xl shadow-green-500/10 border border-white/10">
                <div className="bg-gradient-to-b from-green-600 to-green-700 rounded-t-[1.5rem] sm:rounded-t-[2rem] px-3 sm:px-4 py-2 sm:py-3 flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center text-lg sm:text-xl">
                    🦞
                  </div>
                  <div>
                    <div className="font-semibold text-xs sm:text-sm">QuantClaw</div>
                    <div className="text-[10px] sm:text-xs text-green-200">online</div>
                  </div>
                </div>
                <div className="bg-[#0b141a] rounded-b-[1.5rem] sm:rounded-b-[2rem] p-3 sm:p-4 min-h-[320px] sm:min-h-[400px] space-y-2 sm:space-y-3">
                  {demoConversation.map((msg, i) => (
                    <div
                      key={i}
                      className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm ${
                          msg.sender === 'user'
                            ? 'bg-green-600 text-white rounded-br-none'
                            : 'bg-slate-700 text-slate-100 rounded-bl-none'
                        }`}
                      >
                        {msg.text}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="order-1 md:order-2 text-center md:text-left">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">
                <span className="text-white">One Conversation.</span><br />
                <span className="text-slate-300">Infinite Capabilities.</span>
              </h2>
              <p className="text-slate-300 mb-4 sm:mb-6 text-sm sm:text-base">
                QuantClaw connects to both AILabQuant for market intelligence and Talkoru
                for knowledge synthesis. Ask about stocks, then pivot to health research,
                then summon Benjamin Graham for a debate — all in one chat.
              </p>
              <div className="space-y-3 sm:space-y-4 max-w-sm mx-auto md:mx-0">
                <div className="flex items-start gap-3 text-left">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-400" />
                  </div>
                  <div>
                    <div className="font-medium text-xs sm:text-sm">Market Intelligence</div>
                    <div className="text-slate-500 text-[10px] sm:text-xs">Real-time quotes, filings, deep stats</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 text-left">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Brain className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-400" />
                  </div>
                  <div>
                    <div className="font-medium text-xs sm:text-sm">Persona Debates</div>
                    <div className="text-slate-500 text-[10px] sm:text-xs">Summon AI experts for deep analysis</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 text-left">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-400" />
                  </div>
                  <div>
                    <div className="font-medium text-xs sm:text-sm">Research Synthesis</div>
                    <div className="text-slate-500 text-[10px] sm:text-xs">PubMed, books, papers — summarized</div>
                  </div>
                </div>
              </div>

              <a
                href={magicLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 sm:mt-8 inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-black font-semibold px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl transition-all text-sm sm:text-base"
              >
                <MessageCircle size={20} />
                Start Chatting
                <ChevronRight size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* How to Claw - Commands Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-slate-900/50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4 text-white">How to Claw</h2>
            <p className="text-slate-300 text-sm sm:text-base max-w-xl mx-auto px-2">
              Just message QuantClaw on WhatsApp. Here's what you can do:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {/* Market Commands */}
            <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl sm:rounded-2xl p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <LineChart className="w-5 h-5 text-blue-400" />
                <h3 className="font-semibold text-sm sm:text-base">Market Intelligence</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <code className="bg-slate-900 text-green-400 px-2 py-1 rounded text-xs font-mono whitespace-nowrap">AAPL</code>
                  <span className="text-slate-300 text-xs sm:text-sm">Get stock price & key stats</span>
                </div>
                <div className="flex items-start gap-3">
                  <code className="bg-slate-900 text-green-400 px-2 py-1 rounded text-xs font-mono whitespace-nowrap">stats NVDA</code>
                  <span className="text-slate-300 text-xs sm:text-sm">Deep analysis: Alpha, Beta, RSI</span>
                </div>
                <div className="flex items-start gap-3">
                  <code className="bg-slate-900 text-green-400 px-2 py-1 rounded text-xs font-mono whitespace-nowrap">Why is TSLA up?</code>
                  <span className="text-slate-300 text-xs sm:text-sm">Ask anything about markets</span>
                </div>
              </div>
            </div>

            {/* Knowledge Commands */}
            <div className="bg-slate-800/50 border border-purple-500/20 rounded-xl sm:rounded-2xl p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <Brain className="w-5 h-5 text-purple-400" />
                <h3 className="font-semibold text-sm sm:text-base">Knowledge & Debates</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <code className="bg-slate-900 text-green-400 px-2 py-1 rounded text-xs font-mono whitespace-nowrap">debate [topic]</code>
                  <span className="text-slate-300 text-xs sm:text-sm">Start a Think Tank debate</span>
                </div>
                <div className="flex items-start gap-3">
                  <code className="bg-slate-900 text-green-400 px-2 py-1 rounded text-xs font-mono whitespace-nowrap">ask Graham</code>
                  <span className="text-slate-300 text-xs sm:text-sm">Summon a persona for advice</span>
                </div>
                <div className="flex items-start gap-3">
                  <code className="bg-slate-900 text-green-400 px-2 py-1 rounded text-xs font-mono whitespace-nowrap">research [topic]</code>
                  <span className="text-slate-300 text-xs sm:text-sm">Search books & papers</span>
                </div>
              </div>
            </div>

            {/* Web & Research */}
            <div className="bg-slate-800/50 border border-green-500/20 rounded-xl sm:rounded-2xl p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <Radar className="w-5 h-5 text-green-400" />
                <h3 className="font-semibold text-sm sm:text-base">Web & Research</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <code className="bg-slate-900 text-green-400 px-2 py-1 rounded text-xs font-mono whitespace-nowrap">claw [url]</code>
                  <span className="text-slate-300 text-xs sm:text-sm">Browse & summarize any URL</span>
                </div>
                <div className="flex items-start gap-3">
                  <code className="bg-slate-900 text-green-400 px-2 py-1 rounded text-xs font-mono whitespace-nowrap">news AAPL</code>
                  <span className="text-slate-300 text-xs sm:text-sm">Get latest news for a stock</span>
                </div>
                <div className="flex items-start gap-3">
                  <code className="bg-slate-900 text-green-400 px-2 py-1 rounded text-xs font-mono whitespace-nowrap">pubmed [query]</code>
                  <span className="text-slate-300 text-xs sm:text-sm">Search medical research</span>
                </div>
              </div>
            </div>

            {/* Alerts & Memory */}
            <div className="bg-slate-800/50 border border-amber-500/20 rounded-xl sm:rounded-2xl p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-amber-400" />
                <h3 className="font-semibold text-sm sm:text-base">Alerts & Memory</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <code className="bg-slate-900 text-green-400 px-2 py-1 rounded text-xs font-mono whitespace-nowrap">alert AAPL &gt; 200</code>
                  <span className="text-slate-300 text-xs sm:text-sm">Set price alerts</span>
                </div>
                <div className="flex items-start gap-3">
                  <code className="bg-slate-900 text-green-400 px-2 py-1 rounded text-xs font-mono whitespace-nowrap">remember [note]</code>
                  <span className="text-slate-300 text-xs sm:text-sm">Save to your memory bank</span>
                </div>
                <div className="flex items-start gap-3">
                  <code className="bg-slate-900 text-green-400 px-2 py-1 rounded text-xs font-mono whitespace-nowrap">help</code>
                  <span className="text-slate-300 text-xs sm:text-sm">See all available commands</span>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <a
              href={magicLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-400 text-black font-semibold px-6 py-3 rounded-xl transition-all text-sm sm:text-base"
            >
              <MessageCircle size={18} />
              Try it now on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* BYOK Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-gradient-to-b from-slate-900/50 to-transparent">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-white/10 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center gap-6 sm:gap-8">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0">
                <Key className="w-8 h-8 sm:w-10 sm:h-10 text-amber-400" />
              </div>
              <div className="text-center md:text-left">
                <h2 className="text-xl sm:text-2xl font-bold mb-2 text-white">
                  Your Data. Your Keys. Your Intelligence.
                </h2>
                <p className="text-slate-300 mb-4 text-sm sm:text-base">
                  QuantClaw doesn't charge you for AI tokens. Connect your own Gemini or OpenAI
                  API keys and pay only for what you use. We provide the architecture; you provide the brain.
                </p>
                <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                  <div className="flex items-center gap-2 text-sm text-slate-200">
                    <Shield className="w-4 h-4 text-green-400" />
                    Privacy-first
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-200">
                    <Zap className="w-4 h-4 text-yellow-400" />
                    No token fees
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-200">
                    <Sparkles className="w-4 h-4 text-purple-400" />
                    Full control
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Architecture Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-xl sm:text-2xl font-bold mb-8 sm:mb-12 text-white">The Architecture</h2>

          <div className="relative max-w-sm mx-auto">
            {/* Top - WhatsApp Interface */}
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6">
              <div className="flex items-center justify-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-400" />
                <span className="font-semibold text-sm sm:text-base">QuantClaw Agent</span>
              </div>
              <p className="text-xs sm:text-sm text-slate-300">WhatsApp Interface</p>
            </div>

            {/* Connector */}
            <div className="w-0.5 h-6 sm:h-8 bg-gradient-to-b from-green-500/30 to-slate-500/30 mx-auto" />

            {/* Middle - Bridge */}
            <div className="bg-slate-800/50 border border-white/10 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 inline-block">
              <span className="text-xs sm:text-sm text-slate-300">Intelligence Bridge</span>
            </div>

            {/* Connectors */}
            <div className="flex justify-center gap-6 sm:gap-8 mb-4 sm:mb-6">
              <div className="w-0.5 h-6 sm:h-8 bg-gradient-to-b from-slate-500/30 to-blue-500/30 rotate-[-30deg]" />
              <div className="w-0.5 h-6 sm:h-8 bg-gradient-to-b from-slate-500/30 to-purple-500/30 rotate-[30deg]" />
            </div>

            {/* Bottom - Two Platforms */}
            <div className="grid grid-cols-2 gap-3 sm:gap-6">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg sm:rounded-xl p-3 sm:p-4">
                <LineChart className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 mx-auto mb-1 sm:mb-2" />
                <div className="font-medium text-xs sm:text-sm">AILabQuant</div>
                <div className="text-[10px] sm:text-xs text-slate-500">Market Data</div>
              </div>
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg sm:rounded-xl p-3 sm:p-4">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400 mx-auto mb-1 sm:mb-2" />
                <div className="font-medium text-xs sm:text-sm">Talkoru</div>
                <div className="text-[10px] sm:text-xs text-slate-500">Knowledge Base</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-slate-300 text-xs sm:text-sm uppercase tracking-wider mb-3 sm:mb-4">
            For the Modern Polymath
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-white">
            Ready to meet your<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-400">
              Executive Intelligence Agent?
            </span>
          </h2>
          <a
            href={magicLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-black font-bold px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-base sm:text-lg transition-all active:scale-[0.98] md:hover:scale-[1.02]"
          >
            <MessageCircle size={24} />
            Start on WhatsApp
            <ChevronRight size={20} />
          </a>
          <p className="text-slate-400 text-sm mt-4">
            No signup required. Just say "Hi QuantClaw".
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-10 px-4 sm:px-6 border-t border-white/5">
        <div className="max-w-5xl mx-auto flex flex-col gap-4 items-center text-center">
          <div className="flex items-center gap-2">
            <span className="text-xl sm:text-2xl">🦞</span>
            <span className="font-bold">QuantClaw</span>
            <span className="text-slate-600 text-sm">.ai</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-slate-500">
            <a href="https://ailabquant.com" className="hover:text-white transition-colors">
              AILabQuant
            </a>
            <a href="https://talkoru.com" className="hover:text-white transition-colors">
              Talkoru
            </a>
            <span className="hidden sm:inline text-slate-700">|</span>
            <span className="w-full sm:w-auto text-center">Executive Assistant for the Modern Polymath</span>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};

export default QuantClawLanding;
